/**
 * DraftCompletionBubble
 * ─────────────────────
 * Replaces the raw draft-text bubble that used to appear in the chat
 * panel after a draft was generated. Shows:
 *
 *   1. ✓ checkmark steps for the generation pipeline
 *   2. Optional metadata block — contract type, article count, governing
 *      law, dispute mechanism (when surfaced by the agent)
 *   3. ✓ "Done — draft is ready" success line
 *
 * The full draft text stays in the right-pane `ContractEditor`.
 */
import React from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

const CHECK_STEPS_AR = [
  'تحليل الطلب',
  'تحديد نوع المستند',
  'تحديد الأطراف والإطار القانوني',
  'تطبيق النماذج القانونية',
  'صياغة المواد',
  'مراجعة الجودة',
];
const CHECK_STEPS_EN = [
  'Analyse the request',
  'Identify document type',
  'Identify parties + legal framework',
  'Apply legal templates',
  'Draft articles',
  'Quality review',
];

export function DraftCompletionBubble({ output = {}, isArabic }) {
  const articleCount = countArticles(output.draft_text || '');
  const contractType = output.contract_type || (isArabic ? 'مستند قانوني' : 'Legal document');
  const governingLaw = output.governing_law || (isArabic ? 'الأنظمة السعودية' : 'Saudi laws');
  const arbitration  = output.arbitration_clause || output.dispute_mechanism || (isArabic ? 'القضاء المختص' : 'Competent court');
  const steps = isArabic ? CHECK_STEPS_AR : CHECK_STEPS_EN;

  return (
    <div
      className="text-sm"
      style={{
        background: 'var(--surface)',
        border: 'var(--border-default)',
        color: 'var(--text-primary)',
        borderRadius: '12px',
        maxWidth: '95%',
        padding: '14px 16px',
      }}
      data-testid="draft-completion-bubble"
    >
      {/* Step checks */}
      <ul className={cn('space-y-1.5 mb-3', isArabic && 'text-right')}>
        {steps.map((label) => (
          <li
            key={label}
            className={cn('flex items-center gap-2', isArabic && 'flex-row-reverse')}
            style={{ fontSize: '13px', color: 'var(--text-primary)' }}
          >
            <Check className="w-3.5 h-3.5 shrink-0" strokeWidth={2.4} style={{ color: '#111111' }} />
            <span>{label}</span>
          </li>
        ))}
      </ul>

      {/* Metadata grid */}
      <div
        className="grid grid-cols-2 gap-2 my-3"
        style={{
          background: 'var(--canvas)',
          border: 'var(--border-default)',
          borderRadius: '8px',
          padding: '10px 12px',
          fontSize: '12.5px',
        }}
        data-testid="draft-completion-metadata"
      >
        <Meta isArabic={isArabic} label={isArabic ? 'نوع المستند' : 'Document type'} value={contractType} />
        <Meta isArabic={isArabic} label={isArabic ? 'عدد المواد' : 'Articles'} value={articleCount ? String(articleCount) : '—'} />
        <Meta isArabic={isArabic} label={isArabic ? 'القانون الحاكم' : 'Governing law'} value={governingLaw} />
        <Meta isArabic={isArabic} label={isArabic ? 'آلية التحكيم' : 'Dispute mechanism'} value={arbitration} />
      </div>

      {/* Success line */}
      <div
        className={cn('flex items-center gap-2 mt-2', isArabic && 'flex-row-reverse')}
        style={{ fontSize: '13px', fontWeight: 500, color: '#111111' }}
        data-testid="draft-completion-success"
      >
        <Check className="w-4 h-4" strokeWidth={2.4} />
        <span>{isArabic ? 'تم — المسودة جاهزة' : 'Done — draft is ready'}</span>
      </div>
    </div>
  );
}

function Meta({ label, value, isArabic }) {
  return (
    <div className={cn('flex flex-col', isArabic && 'text-right')}>
      <span style={{ color: 'var(--text-hint)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </span>
      <span style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 500, marginTop: '2px' }}>
        {value}
      </span>
    </div>
  );
}

function countArticles(markdown) {
  if (!markdown) return 0;
  // Count ## headings or "المادة" / "Article" lines
  const m = markdown.match(/^##\s+/gm);
  if (m) return m.length;
  const arabicArt = markdown.match(/(?:^|\n)\s*المادة\s+\d+/g);
  if (arabicArt) return arabicArt.length;
  const englishArt = markdown.match(/(?:^|\n)\s*Article\s+\d+/gi);
  if (englishArt) return englishArt.length;
  return 0;
}

export default DraftCompletionBubble;
